import { Component, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

/**
 * How many date inputs an operator needs. Drives which inputs the panel shows
 * and what isFilterActive() requires before the filter counts as "set".
 */
type OperandCount = 0 | 1 | 2;

interface DateOperator {
  /** Stable key — persisted in saved reports. NEVER change once shipped. */
  key: string;
  label: string;
  operands: OperandCount;
}

/**
 * The operator list, in the order Ray asked for it. The first five are the
 * comparison operators AG Grid's own agDateColumnFilter provides; the rest are
 * the relative presets it does not. Keeping both in one filter is the whole
 * point — this component REPLACES agDateColumnFilter on date columns, so
 * anything missing here is simply unavailable to the user.
 *
 * Legacy keys ('past-due', 'next-30', …) are preserved exactly so reports saved
 * by the earlier presets-only version keep working.
 */
export const DATE_FILTER_OPERATORS: DateOperator[] = [
  { key: 'not-equals', label: 'Does Not Equal', operands: 1 },
  { key: 'equals',     label: 'Equals',         operands: 1 },
  { key: 'before',     label: 'Before',         operands: 1 },
  { key: 'after',      label: 'After',          operands: 1 },
  { key: 'between',    label: 'Between',        operands: 2 },
  { key: 'past-due',   label: 'Past Due',       operands: 0 },
  { key: 'next-30',    label: 'Next 30 Days',   operands: 0 },
  { key: 'next-60',    label: 'Next 60 Days',   operands: 0 },
  { key: 'next-90',    label: 'Next 90 Days',   operands: 0 },
  { key: 'next-120',   label: 'Next 120 Days',  operands: 0 },
  { key: 'next-180',   label: 'Next 180 Days',  operands: 0 },
  { key: 'blank',      label: 'Blank',          operands: 0 },
];

/** Relative presets, expressed as a day offset window from today. */
const PRESET_WINDOWS: Record<string, number> = {
  'next-30': 30,
  'next-60': 60,
  'next-90': 90,
  'next-120': 120,
  'next-180': 180,
};

/** The persisted filter model. `from`/`to` are 'YYYY-MM-DD' so they survive JSON. */
export interface DateRangeFilterModel {
  type: 'dateRangePreset';
  op: string;
  from?: string;
  to?: string;
  /** @deprecated Legacy field from the presets-only version; read, never written. */
  preset?: string;
}

/**
 * Human-readable one-liner for a saved model — used by the report builder's
 * filter debug panel so the description stays in step with the operator list.
 */
export function describeDateFilterModel(model: any): string {
  const op = model?.op || model?.preset;
  const def = DATE_FILTER_OPERATORS.find(o => o.key === op);
  if (!def) return `date filter (${op})`;
  if (def.operands === 2) return `${def.label.toLowerCase()} ${model.from || '?'} and ${model.to || '?'}`;
  if (def.operands === 1) return `${def.label.toLowerCase()} ${model.from || '?'}`;
  return def.label.toLowerCase();
}

/**
 * Swap agDateColumnFilter for this component on every date column.
 *
 * Shared so the report BUILDER and the report PREVIEW agree. They previously
 * did not: the builder swapped the filter in and the preview used the raw
 * columns, so a report saved with a preset filter produced a model that the
 * preview's agDateColumnFilter could not read — the date filter silently did
 * nothing when the report was actually run.
 */
export function applyDateRangeFilter(columns: any[]): any[] {
  return (columns || []).map((col: any) =>
    col?.filter === 'agDateColumnFilter'
      ? { ...col, filter: DateRangeFilterComponent }
      : { ...col }
  );
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="date-range-filter">
      <div class="filter-title">Date Filter</div>

      <select class="op-select" [(ngModel)]="op" (ngModelChange)="onOperatorChange()">
        <option [ngValue]="null">-- Select --</option>
        @for (o of operators; track o.key) {
          <option [ngValue]="o.key">{{ o.label }}</option>
        }
      </select>

      @if (operandCount() >= 1) {
        <label class="field-label">{{ operandCount() === 2 ? 'From' : 'Date' }}</label>
        <input type="date" class="date-input" [(ngModel)]="from" (ngModelChange)="onValueChange()">
      }
      @if (operandCount() === 2) {
        <label class="field-label">To</label>
        <input type="date" class="date-input" [(ngModel)]="to" (ngModelChange)="onValueChange()">
      }

      @if (op && !isFilterActive()) {
        <div class="hint">Enter {{ operandCount() === 2 ? 'both dates' : 'a date' }} to apply.</div>
      }

      <button class="clear-btn" [class.visible]="op !== null" (click)="clearFilter()">
        Clear Filter
      </button>
    </div>
  `,
  styles: [`
    .date-range-filter { padding: 12px; min-width: 200px; display: flex; flex-direction: column; }
    .filter-title {
      font-size: 12px; font-weight: 600; color: #666; text-transform: uppercase;
      letter-spacing: 0.5px; margin-bottom: 8px;
    }
    .op-select, .date-input {
      width: 100%; padding: 7px 10px; border: 1px solid #e0e0e0; border-radius: 6px;
      background: #fff; font-size: 13px; color: #333; box-sizing: border-box; outline: none;
    }
    .op-select:focus, .date-input:focus { border-color: #1976d2; }
    .field-label { font-size: 11px; color: #757575; margin: 8px 0 3px; }
    .hint { font-size: 11px; color: #ef6c00; margin-top: 8px; }
    .clear-btn {
      width: 100%; margin-top: 10px; padding: 6px 12px; border: none; border-radius: 6px;
      background: transparent; font-size: 12px; color: #f44336; cursor: pointer;
      opacity: 0; transition: opacity 0.15s ease;
    }
    .clear-btn.visible { opacity: 1; }
    .clear-btn:hover { background: #ffebee; }
  `]
})
export class DateRangeFilterComponent implements IFilterAngularComp {
  private cdr = inject(ChangeDetectorRef);
  private params!: IFilterParams;

  readonly operators = DATE_FILTER_OPERATORS;

  op: string | null = null;
  /** 'YYYY-MM-DD' from the native date inputs. */
  from = '';
  to = '';

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  operandCount(): OperandCount {
    return DATE_FILTER_OPERATORS.find(o => o.key === this.op)?.operands ?? 0;
  }

  /**
   * A half-entered filter (operator picked, date still blank) must NOT be active
   * — otherwise the grid would filter every row out while the user is typing.
   */
  isFilterActive(): boolean {
    if (!this.op) return false;
    const needed = this.operandCount();
    if (needed >= 1 && !this.from) return false;
    if (needed === 2 && !this.to) return false;
    return true;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this.isFilterActive()) return true;

    const field = this.params.colDef.field;
    if (!field) return true;

    const cell = this.toLocalDay(params.data?.[field]);

    // 'Blank' is the only operator that matches empty cells. Every other one
    // excludes them — a row with no date shouldn't surface under "Does Not
    // Equal 1/1/26" just because it's technically not equal.
    if (this.op === 'blank') return cell === null;
    if (cell === null) return false;

    const today = this.startOfToday().getTime();
    const target = this.toLocalDay(this.from);

    switch (this.op) {
      case 'equals':      return target !== null && cell === target;
      case 'not-equals':  return target !== null && cell !== target;
      case 'before':      return target !== null && cell < target;
      case 'after':       return target !== null && cell > target;
      case 'between': {
        const end = this.toLocalDay(this.to);
        if (target === null || end === null) return true;
        // Tolerate the user entering the dates backwards.
        const lo = Math.min(target, end);
        const hi = Math.max(target, end);
        return cell >= lo && cell <= hi;
      }
      case 'past-due':    return cell < today;
      default: {
        const days = PRESET_WINDOWS[this.op!];
        if (days === undefined) return true;
        return cell >= today && cell <= this.daysFromToday(days);
      }
    }
  }

  getModel(): DateRangeFilterModel | null {
    if (!this.isFilterActive()) return null;
    const model: DateRangeFilterModel = { type: 'dateRangePreset', op: this.op! };
    if (this.operandCount() >= 1) model.from = this.from;
    if (this.operandCount() === 2) model.to = this.to;
    return model;
  }

  setModel(model: any): void {
    if (!model) {
      this.op = null;
      this.from = '';
      this.to = '';
    } else {
      // `preset` is the legacy presets-only shape — keep reading it so reports
      // saved before the operators landed still apply their date filter.
      this.op = model.op || model.preset || null;
      this.from = model.from || '';
      this.to = model.to || '';
    }
    this.cdr.markForCheck();
  }

  onOperatorChange(): void {
    // Dropping to an operator that needs fewer dates shouldn't leave stale ones
    // in the model.
    const needed = this.operandCount();
    if (needed < 2) this.to = '';
    if (needed < 1) this.from = '';
    this.params.filterChangedCallback();
  }

  onValueChange(): void {
    this.params.filterChangedCallback();
  }

  clearFilter(): void {
    this.op = null;
    this.from = '';
    this.to = '';
    this.params.filterChangedCallback();
  }

  /**
   * Normalize any cell/input value to local midnight as an epoch ms, so all
   * comparisons are day-granular. Date-only strings ('YYYY-MM-DD') are parsed
   * as LOCAL midnight — `new Date('2026-04-01')` parses as UTC and lands on
   * 3/31 in US timezones, which is the off-by-one that bites date handling
   * elsewhere in this codebase.
   */
  private toLocalDay(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'string') {
      const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
      if (dateOnly) {
        return new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]).getTime();
      }
    }

    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private daysFromToday(days: number): number {
    const d = this.startOfToday();
    d.setDate(d.getDate() + days);
    return d.getTime();
  }
}
